import { ShoppingCart, Trash2 } from 'lucide-react';
import { useKitBuilder } from '../../context/KitBuilderContext';
import KitItemThumbnail from './KitItemThumbnail';

const orderedTypes = ['film', 'container', 'deli', 'sealing'];

const KitReviewPage = ({ onNavigate }) => {
  const {
    kitItems,
    kitFeedback,
    handleKitSubmit,
    kitContact,
    setKitContact,
    kitSubmitting,
    kitTypeLabels,
    getKitItemImageUrl,
    handleRemoveKitItem
  } = useKitBuilder();

  if (kitItems.length === 0) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-10 text-center space-y-6">
          <div className="flex justify-center">
            <div className="h-20 w-20 rounded-full bg-orange-100 flex items-center justify-center">
              <ShoppingCart size={36} className="text-brandOrange" />
            </div>
          </div>
          <h2 className="text-4xl font-bold text-gray-800 uppercase">Build Your Kit</h2>
          {kitFeedback && (
            <div
              className={`rounded-2xl border px-4 py-3 text-lg font-semibold ${
                kitFeedback.type === 'success'
                  ? 'border-green-200 bg-green-50 text-green-700'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}
            >
              {kitFeedback.message}
            </div>
          )}
          <p className="text-lg text-gray-600">
            Your kit is currently empty. Explore our films, containers, and sealing machines to add products before reviewing.
          </p>
          <div className="flex flex-col md:flex-row justify-center gap-4">
            <button
              onClick={() => onNavigate('films')}
              className="w-full md:w-auto rounded-full border-2 border-brandOrange text-brandOrange px-6 py-3 text-lg font-semibold uppercase hover:bg-orange-50 transition-colors"
            >
              Browse Films
            </button>
            <button
              onClick={() => onNavigate('containers')}
              className="w-full md:w-auto rounded-full border-2 border-brandOrange text-brandOrange px-6 py-3 text-lg font-semibold uppercase hover:bg-orange-50 transition-colors"
            >
              Browse Containers
            </button>
            <button
              onClick={() => onNavigate('sealing')}
              className="w-full md:w-auto rounded-full border-2 border-brandOrange text-brandOrange px-6 py-3 text-lg font-semibold uppercase hover:bg-orange-50 transition-colors"
            >
              Sealing Machines
            </button>
          </div>
        </div>
      </div>
    );
  }

  const groupedItems = kitItems.reduce((acc, item) => {
    const key = item.type;
    if (!acc[key]) {
      acc[key] = [];
    }
    acc[key].push(item);
    return acc;
  }, {});

  const sectionsToRender = orderedTypes.filter((type) => groupedItems[type]?.length);

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-8">
          <div>
            <p className="text-sm uppercase text-brandOrange tracking-wide font-semibold">Review Kit</p>
            <h2 className="text-4xl md:text-5xl font-bold text-gray-800 uppercase">
              Your Turnkey <span className="text-brandOrange">Packaging Kit</span>
            </h2>
            <p className="text-lg text-gray-600 mt-2">
              <strong className="text-brandOrange">{kitItems.length} item{kitItems.length > 1 ? 's' : ''}</strong> ready to share with the Teinnovations team.
            </p>
          </div>
          <button
            onClick={() => onNavigate('films')}
            className="rounded-full border-2 border-brandOrange text-brandOrange px-6 py-3 text-lg font-semibold uppercase hover:bg-orange-50 transition-colors"
          >
            Add More Items
          </button>
        </div>

        <div className="space-y-10">
          {sectionsToRender.map((type) => (
            <div key={type} className="space-y-4">
              <h3 className="text-2xl font-semibold text-gray-800 uppercase border-l-4 border-brandOrange pl-4">
                {kitTypeLabels[type]}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {groupedItems[type].map((item) => (
                  <div
                    key={`${type}-${item.id}`}
                    className="border border-gray-200 rounded-2xl p-5 flex items-center gap-5 bg-gray-50 hover:bg-orange-50 hover:border-brandOrange transition-colors"
                  >
                    <KitItemThumbnail item={item} getImageUrl={getKitItemImageUrl} />
                    <div className="flex-1">
                      <p className="text-xl font-semibold text-gray-800">{item.title}</p>
                      <p className="text-sm text-gray-600">{item.details || 'Details supplied at booth'}</p>
                    </div>
                    <button
                      onClick={() => handleRemoveKitItem(type, item.id)}
                      className="flex items-center gap-2 rounded-full border border-gray-300 px-3 py-1 text-sm font-semibold text-gray-600 hover:bg-orange-100 hover:border-brandOrange hover:text-brandOrange transition-colors"
                    >
                      <Trash2 size={16} />
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8 md:p-12">
        <div className="mb-6">
          <h3 className="text-3xl font-bold text-gray-800 uppercase">Finalize & Submit Your Kit!</h3>
          <p className="text-lg text-gray-600">
            Share your contact details, any notes, and submit your kit! The Teinnovations team will follow up ASAP to walk you through what comes next.
          </p>
        </div>

        {kitFeedback && (
          <div
            className={`mb-6 rounded-2xl border px-4 py-3 text-lg font-semibold ${
              kitFeedback.type === 'success'
                ? 'border-green-200 bg-green-50 text-green-700'
                : 'border-red-200 bg-red-50 text-red-700'
            }`}
          >
            {kitFeedback.message}
          </div>
        )}

        <form onSubmit={handleKitSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Name *</label>
              <input
                type="text"
                required
                value={kitContact.name}
                onChange={(e) => setKitContact({ ...kitContact, name: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Company</label>
              <input
                type="text"
                value={kitContact.company}
                onChange={(e) => setKitContact({ ...kitContact, company: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Email *</label>
              <input
                type="email"
                required
                value={kitContact.email}
                onChange={(e) => setKitContact({ ...kitContact, email: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700">Phone</label>
              <input
                type="tel"
                value={kitContact.phone}
                onChange={(e) => setKitContact({ ...kitContact, phone: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700">Notes for the Teinnovations Team</label>
            <textarea
              rows={4}
              value={kitContact.notes}
              onChange={(e) => setKitContact({ ...kitContact, notes: e.target.value })}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Share any project details, timelines, or follow-up preferences."
            />
          </div>

          <button
            type="submit"
            disabled={kitSubmitting}
            className={`w-full text-white py-4 rounded-xl text-xl font-semibold uppercase tracking-wide transition-transform duration-200 ${
              kitSubmitting ? 'bg-gray-300 cursor-wait' : 'shadow-lg hover:scale-[1.02]'
            }`}
            style={
              kitSubmitting
                ? {}
                : {
                    background:
                      'linear-gradient(270deg,#ffa400,#f78900 15.67%,#f07100 33.69%,#eb6000 52.89%,#e85600 73.87%,#e75300)'
                  }
            }
          >
            {kitSubmitting ? 'Sending Kit...' : 'Submit Kit'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default KitReviewPage;
